import { useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type KeyboardEvent, type MouseEvent as ReactMouseEvent } from "react";
import { deriveClues, cluesEqual, completedClueFlags } from "../../shared/nonogram/clues";
import type { CellMark, Puzzle } from "../../shared/nonogram/types";
import type { Tool } from "../game/useGame";

type BoardMode = "draw" | "move";

type Props = {
  puzzle: Puzzle;
  cells: CellMark[];
  tool: Tool;
  boardMode: BoardMode;
  completed: boolean;
  mistakeCell?: number;
  mistakeCells?: number[];
  zoom: number;
  beginStroke: () => void;
  paintCell: (index: number, tool?: Tool, mode?: "toggle" | "absolute", forcedMark?: CellMark) => void;
  endStroke: () => void;
  makeMove: (index: number, tool?: Tool) => void;
  /** Where the tap-cycle assist sends a cell: its next state comes from the cell
      itself, so a single tap walks empty → fill → cross → empty. */
  cycleMark: (existing: CellMark) => CellMark;
  /** Whether a short tap cycles rather than applying the selected tool. */
  tapCycles: boolean;
  /** The panel owns the status strip under the grid, and "this board pans" is a
      status. The board reports its overflow instead of printing its own line
      inside the scrollport, where the message scrolled away with the grid. */
  onPannableChange?: (pannable: boolean) => void;
};

type Guide = { row: number; column: number };

const LONG_PRESS_MS = 400;

function markLabel(mark: CellMark): string {
  if (mark === 1) return "filled";
  if (mark === -1) return "crossed out";
  return "empty";
}

function clueLabel(clues: readonly number[] | undefined): string {
  return clues?.length ? clues.join(" ") : "0";
}

export function GameBoard({
  puzzle,
  cells,
  tool,
  boardMode,
  completed,
  mistakeCell,
  mistakeCells,
  zoom,
  beginStroke,
  paintCell,
  endStroke,
  makeMove,
  cycleMark,
  tapCycles,
  onPannableChange,
}: Props) {
  const [focusedCell, setFocusedCell] = useState(0);
  const [guide, setGuide] = useState<Guide>();
  const [pannable, setPannable] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const wasCompleted = useRef(completed);
  const reportPannable = useRef(onPannableChange);
  reportPannable.current = onPannableChange;
  const scrollRef = useRef<HTMLDivElement>(null);
  const activePointer = useRef<number | undefined>(undefined);
  const visited = useRef(new Set<number>());
  const strokeTool = useRef<Tool>(tool);
  const longPressTimer = useRef<number | undefined>(undefined);
  const longPressArmed = useRef(false);
  const longPressOrigin = useRef<{ x: number; y: number; index: number } | undefined>(undefined);
  const panPointer = useRef<number | undefined>(undefined);
  const panLast = useRef<{ x: number; y: number } | undefined>(undefined);
  const mistakeSet = useMemo(() => {
    const values = mistakeCells && mistakeCells.length > 0 ? mistakeCells : mistakeCell === undefined ? [] : [mistakeCell];
    return new Set(values);
  }, [mistakeCell, mistakeCells]);
  const activeGuide = completed ? undefined : guide;
  const currentClues = useMemo(
    () => deriveClues(cells.map((cell) => (cell === 1 ? 1 : 0)), puzzle.size),
    [cells, puzzle.size],
  );
  const completedRowClues = currentClues.rowClues.map((actual, index) =>
    completedClueFlags(actual, puzzle.rowClues[index] ?? []),
  );
  const completedColumnClues = currentClues.columnClues.map((actual, index) =>
    completedClueFlags(actual, puzzle.columnClues[index] ?? []),
  );
  const completedRows = currentClues.rowClues.map(
    (clue, index) => (puzzle.rowClues[index]?.length ?? 0) > 0 && cluesEqual(clue, puzzle.rowClues[index] ?? []),
  );
  const completedColumns = currentClues.columnClues.map(
    (clue, index) => (puzzle.columnClues[index]?.length ?? 0) > 0 && cluesEqual(clue, puzzle.columnClues[index] ?? []),
  );

  useEffect(() => {
    if (completed && !wasCompleted.current) setCelebrate(true);
    if (!completed) setCelebrate(false);
    wasCompleted.current = completed;
  }, [completed]);

  // Overflow is expected on large boards. Painting keeps touch-action: none on the grid;
  // horizontal pan uses the clue gutters, and two-finger swipes on the scrollport.
  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return undefined;
    const scrollport: HTMLDivElement = element;
    function measure(): void {
      const next = scrollport.scrollWidth > scrollport.clientWidth + 1;
      setPannable(next);
      reportPannable.current?.(next);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    measure();
    return () => observer.disconnect();
  }, [puzzle.size, zoom]);

  // The corner map's frame: which slice of the grid is on screen. Fractions of the
  // whole, so the map can be any size without recomputing anything.
  const [viewFrame, setViewFrame] = useState({ left: 0, top: 0, width: 1, height: 1 });
  // Where the map itself sits: pinned to the visible bottom-right corner of the
  // board. Sticky cannot do this — the board's scrollport scrolls horizontally
  // while the page scrolls vertically, and sticky only answers to one ancestor
  // per axis. Fixed plus this measurement answers to both.
  const [mapPos, setMapPos] = useState<{ left: number; top: number }>();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapVisible = pannable && puzzle.size >= 20;
  // A map that covers working squares is worse than no map, so folding it away is
  // a real preference — remembered, not re-argued on every visit.
  const [mapCollapsed, setMapCollapsed] = useState(() => {
    try {
      return localStorage.getItem("nonogram:v1:board-map-collapsed") === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("nonogram:v1:board-map-collapsed", mapCollapsed ? "1" : "0");
    } catch {
      // Preference is optional.
    }
  }, [mapCollapsed]);
  useEffect(() => {
    const element = scrollRef.current;
    if (!element || !mapVisible) return undefined;
    let raf = 0;
    const update = (): void => {
      raf = 0;
      const maxY = Math.max(1, element.scrollHeight - element.clientHeight);
      setViewFrame({
        left: element.scrollLeft / element.scrollWidth,
        top: element.scrollTop / maxY,
        width: element.clientWidth / element.scrollWidth,
        height: element.clientHeight / element.scrollHeight,
      });
    };
    const schedule = (): void => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    element.addEventListener("scroll", schedule, { passive: true });
    const observer = new ResizeObserver(schedule);
    observer.observe(element);
    schedule();
    return () => {
      element.removeEventListener("scroll", schedule);
      observer.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [mapVisible, puzzle.size, zoom]);

  // The map's own anchor: the bottom-right of whichever part of the board is on
  // screen. Hides itself when too little of the board is left to be worth jumping
  // around — the page below the board has no use for a map.
  useEffect(() => {
    if (!mapVisible) return undefined;
    let raf = 0;
    const update = (): void => {
      raf = 0;
      const board = scrollRef.current;
      const map = mapRef.current;
      if (!board) return;
      const rect = board.getBoundingClientRect();
      const visibleTop = Math.max(rect.top, 0);
      const visibleBottom = Math.min(rect.bottom, window.innerHeight);
      if (visibleBottom - visibleTop < 240 || rect.right < 0 || rect.left > window.innerWidth) {
        setMapPos(undefined);
        return;
      }
      const mapW = map ? map.offsetWidth + 6 : 110;
      const mapH = map ? map.offsetHeight + 6 : 110;
      setMapPos({
        left: Math.round(rect.right - mapW),
        top: Math.round(visibleBottom - mapH),
      });
    };
    const schedule = (): void => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    const observer = new ResizeObserver(schedule);
    if (scrollRef.current) observer.observe(scrollRef.current);
    schedule();
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      observer.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [mapVisible, mapCollapsed, zoom]);

  /** Tap the map to bring that region of the grid on screen. */
  function mapJump(event: ReactPointerEvent<HTMLDivElement>): void {
    const element = scrollRef.current;
    if (!element) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const fractionX = (event.clientX - bounds.left) / bounds.width;
    const fractionY = (event.clientY - bounds.top) / bounds.height;
    element.scrollTo({
      left: fractionX * element.scrollWidth - element.clientWidth / 2,
      top: fractionY * element.scrollHeight - element.clientHeight / 2,
      behavior: "smooth",
    });
  }

  useEffect(() => {
    const node = scrollRef.current;
    if (!node || !pannable) return undefined;
    const scrollport: HTMLDivElement = node;
    let lastX = 0;
    let lastY = 0;
    function onTouchStart(event: TouchEvent): void {
      const first = event.touches.item(0);
      const second = event.touches.item(1);
      if (!first || !second) return;
      lastX = (first.clientX + second.clientX) / 2;
      lastY = (first.clientY + second.clientY) / 2;
    }
    // Two fingers move the whole view: the board sideways, the page up and down. The
    // grid keeps touch-action: none so a one-finger stroke paints, which also means the
    // browser will not scroll the page from here — so this has to.
    function onTouchMove(event: TouchEvent): void {
      const first = event.touches.item(0);
      const second = event.touches.item(1);
      if (!first || !second) return;
      event.preventDefault();
      const x = (first.clientX + second.clientX) / 2;
      const y = (first.clientY + second.clientY) / 2;
      scrollport.scrollLeft -= x - lastX;
      window.scrollBy(0, lastY - y);
      lastX = x;
      lastY = y;
    }
    // iOS Safari zooms the page on a two-finger gesture even when touchmove is
    // prevented, and the zoomed page then sits parked to one side — the header, the
    // date and the left half of the toolbar off-screen, which reads as a broken
    // layout rather than as a zoom. Gesture events are the only hook that stops it.
    // Only the board is guarded: pinch-zoom still works everywhere else on the page.
    function blockGesture(event: Event): void {
      event.preventDefault();
    }
    scrollport.addEventListener("touchstart", onTouchStart, { passive: true });
    scrollport.addEventListener("touchmove", onTouchMove, { passive: false });
    scrollport.addEventListener("gesturestart", blockGesture);
    scrollport.addEventListener("gesturechange", blockGesture);
    scrollport.addEventListener("gestureend", blockGesture);
    return () => {
      scrollport.removeEventListener("touchstart", onTouchStart);
      scrollport.removeEventListener("touchmove", onTouchMove);
      scrollport.removeEventListener("gesturestart", blockGesture);
      scrollport.removeEventListener("gesturechange", blockGesture);
      scrollport.removeEventListener("gestureend", blockGesture);
    };
  }, [pannable, puzzle.size, zoom]);

  useEffect(() => () => window.clearTimeout(longPressTimer.current), []);

  function clearLongPress(): void {
    window.clearTimeout(longPressTimer.current);
    longPressTimer.current = undefined;
    longPressArmed.current = false;
    longPressOrigin.current = undefined;
  }

  function startStroke(index: number, paintTool: Tool, pointerId: number, target: HTMLElement): void {
    activePointer.current = pointerId;
    visited.current = new Set([index]);
    const current = cells[index] ?? 0;
    const selectedMark = paintTool === "fill" ? 1 : paintTool === "cross" ? -1 : 0;
    // Lock the stroke from the first cell: painting a mark onto itself means erase for this drag.
    strokeTool.current = current === selectedMark && paintTool !== "erase" ? "erase" : paintTool;
    target.closest("[data-board]")?.setPointerCapture(pointerId);
    beginStroke();
    paintCell(index, strokeTool.current, "absolute");
  }

  function startPan(event: ReactPointerEvent<HTMLButtonElement>): void {
    panPointer.current = event.pointerId;
    panLast.current = { x: event.clientX, y: event.clientY };
    event.currentTarget.closest("[data-board]")?.setPointerCapture(event.pointerId);
  }

  function pointerDown(event: ReactPointerEvent<HTMLButtonElement>, index: number): void {
    if (completed) return;
    if (event.button === 2) return;
    if (event.button !== 0) return;

    // Move: one-finger/mouse drag pans the scrollport; never paint or long-press.
    if (boardMode === "move") {
      event.preventDefault();
      clearLongPress();
      startPan(event);
      return;
    }

    // Touch long-press paints a cross stroke without changing the selected tool.
    if (event.pointerType === "touch") {
      clearLongPress();
      longPressOrigin.current = { x: event.clientX, y: event.clientY, index };
      longPressTimer.current = window.setTimeout(() => {
        if (!longPressOrigin.current || activePointer.current !== undefined) return;
        longPressArmed.current = true;
        const cell = longPressOrigin.current.index;
        const button = document.querySelector<HTMLElement>(`[data-cell-index="${cell}"]`);
        if (!button) return;
        startStroke(cell, "cross", event.pointerId, button);
      }, LONG_PRESS_MS);
      return;
    }

    event.preventDefault();
    if (tapCycles) {
      // Hold the stroke until pointer-up, the same way touch does: a click then
      // cycles the cell, while a drag that leaves the square still paints with the
      // selected tool (the move handler picks the stroke up from this origin).
      longPressOrigin.current = { x: event.clientX, y: event.clientY, index };
      return;
    }
    startStroke(index, tool, event.pointerId, event.currentTarget);
  }

  function pointerMove(event: ReactPointerEvent<HTMLDivElement>): void {
    if (panPointer.current === event.pointerId && panLast.current && scrollRef.current) {
      event.preventDefault();
      // Horizontal only: the scrollport does not scroll vertically, and in Move mode
      // touch-action hands vertical drags to the browser so the page scrolls natively.
      const dx = event.clientX - panLast.current.x;
      scrollRef.current.scrollLeft -= dx;
      panLast.current = { x: event.clientX, y: event.clientY };
      return;
    }

    if (longPressOrigin.current && activePointer.current === undefined && !longPressArmed.current) {
      const dx = event.clientX - longPressOrigin.current.x;
      const dy = event.clientY - longPressOrigin.current.y;
      if (Math.hypot(dx, dy) > 10) {
        // Movement cancels long-press and starts a normal drag with the selected tool.
        const index = longPressOrigin.current.index;
        clearLongPress();
        const button = document.querySelector<HTMLElement>(`[data-cell-index="${index}"]`);
        if (button) startStroke(index, tool, event.pointerId, button);
      }
      return;
    }

    if (activePointer.current !== event.pointerId) return;
    event.preventDefault();
    const target = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>("[data-cell-index]");
    const index = Number(target?.dataset.cellIndex);
    if (!Number.isInteger(index) || visited.current.has(index)) return;
    visited.current.add(index);
    paintCell(index, strokeTool.current, "absolute");
  }

  function pointerUp(event: ReactPointerEvent<HTMLDivElement>): void {
    if (panPointer.current === event.pointerId) {
      panPointer.current = undefined;
      panLast.current = undefined;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
      return;
    }

    if (longPressOrigin.current && activePointer.current === undefined && !longPressArmed.current) {
      // Short tap: apply the selected tool once — or, with the assist on, walk the
      // cell's own cycle, which is the whole gesture on a phone.
      const index = longPressOrigin.current.index;
      clearLongPress();
      beginStroke();
      paintCell(index, tool, "toggle", tapCycles ? cycleMark(cells[index] ?? 0) : undefined);
      endStroke();
      return;
    }

    clearLongPress();
    if (activePointer.current !== event.pointerId) return;
    activePointer.current = undefined;
    visited.current.clear();
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    endStroke();
  }

  function contextMenu(event: ReactMouseEvent<HTMLDivElement>): void {
    event.preventDefault();
    if (completed) return;
    const target = (event.target as HTMLElement).closest<HTMLElement>("[data-cell-index]");
    const index = Number(target?.dataset.cellIndex);
    if (!Number.isInteger(index)) return;
    makeMove(index, "cross");
  }

  function trackGuide(index: number): void {
    const row = Math.floor(index / puzzle.size) + 1;
    const column = (index % puzzle.size) + 1;
    setGuide((current) => (current?.row === row && current.column === column ? current : { row, column }));
  }

  function hoverGuide(event: ReactPointerEvent<HTMLDivElement>): void {
    if (event.pointerType === "touch") return;
    const target = (event.target as HTMLElement).closest<HTMLElement>("[data-cell-index]");
    const index = Number(target?.dataset.cellIndex);
    if (Number.isInteger(index)) trackGuide(index);
  }

  function keyDown(event: KeyboardEvent<HTMLButtonElement>, index: number): void {
    const row = Math.floor(index / puzzle.size);
    const column = index % puzzle.size;
    let next = index;
    if (event.key === "ArrowUp") next = Math.max(0, row - 1) * puzzle.size + column;
    else if (event.key === "ArrowDown") next = Math.min(puzzle.size - 1, row + 1) * puzzle.size + column;
    else if (event.key === "ArrowLeft") next = row * puzzle.size + Math.max(0, column - 1);
    else if (event.key === "ArrowRight") next = row * puzzle.size + Math.min(puzzle.size - 1, column + 1);
    // Space and Enter apply whichever tool is selected; the letters are explicit overrides.
    else if (event.key === " " || event.key === "Enter") makeMove(index);
    else if (event.key.toLowerCase() === "f") makeMove(index, "fill");
    else if (event.key.toLowerCase() === "x") makeMove(index, "cross");
    else if (event.key.toLowerCase() === "e" || event.key === "Backspace" || event.key === "Delete") {
      makeMove(index, "erase");
    }
    else return;
    event.preventDefault();
    if (next !== index) {
      setFocusedCell(next);
      trackGuide(next);
      document.querySelector<HTMLButtonElement>(`[data-cell-index="${next}"]`)?.focus();
    }
  }

  return (
    <div
      className="board-scroll"
      ref={scrollRef}
      data-pannable={pannable ? "true" : undefined}
      data-board-mode={boardMode}
      role="group"
      aria-label={`${puzzle.size} by ${puzzle.size} nonogram board`}
      aria-describedby="puzzle-instructions"
    >
      <div
        className="board-layout"
        data-size={puzzle.size}
        data-solved={completed ? "true" : undefined}
        data-celebrate={celebrate ? "true" : undefined}
        style={{
          "--zoom": zoom,
          "--size": puzzle.size,
          "--cell-base": puzzle.size > 15 ? `clamp(1.1rem, min((100vw - 6.5rem) / ${puzzle.size}, (100dvh - 16.5rem) / ${puzzle.size}), 2.1rem)` : undefined,
        } as CSSProperties}
      >
        <div className="clue-corner" aria-hidden="true"><span>{puzzle.size}×{puzzle.size}</span></div>
        <div className="column-clues" aria-hidden="true">
          {puzzle.columnClues.map((clues, index) => (
            <div
              className={`clue${completedColumns[index] ? " is-complete" : ""}${activeGuide?.column === index + 1 ? " is-guide" : ""}`}
              key={`column-${index}`}
            >
              {(clues.length ? clues : [0]).map((clue, clueIndex) => (
                <span className={completedColumnClues[index]?.[clueIndex] ? "is-complete" : undefined} key={clueIndex}>{clue}</span>
              ))}
            </div>
          ))}
        </div>
        <div className="row-clues" aria-hidden="true">
          {puzzle.rowClues.map((clues, index) => (
            <div
              className={`clue${completedRows[index] ? " is-complete" : ""}${activeGuide?.row === index + 1 ? " is-guide" : ""}`}
              key={`row-${index}`}
            >
              {(clues.length ? clues : [0]).map((clue, clueIndex) => (
                <span className={completedRowClues[index]?.[clueIndex] ? "is-complete" : undefined} key={clueIndex}>{clue}</span>
              ))}
            </div>
          ))}
        </div>
        <div
          className="game-grid"
          data-board
          onPointerMove={pointerMove}
          onPointerUp={pointerUp}
          onPointerCancel={pointerUp}
          onPointerOver={hoverGuide}
          onPointerLeave={() => setGuide(undefined)}
          onContextMenu={contextMenu}
        >
          {cells.map((mark, index) => {
            const row = Math.floor(index / puzzle.size) + 1;
            const column = (index % puzzle.size) + 1;
            const onGuide = activeGuide && (activeGuide.row === row || activeGuide.column === column);
            return (
              <button
                className={[
                  "cell",
                  `mark-${mark === 1 ? "fill" : mark === -1 ? "cross" : "empty"}`,
                  onGuide ? "is-guide" : "",
                  mistakeSet.has(index) ? "is-mistake" : "",
                ].filter(Boolean).join(" ")}
                data-cell-index={index}
                data-cell-row={row}
                data-cell-column={column}
                style={{ "--r": row, "--c": column } as CSSProperties}
                key={index}
                type="button"
                aria-label={`Row ${row} clues ${clueLabel(puzzle.rowClues[row - 1])}, column ${column} clues ${clueLabel(puzzle.columnClues[column - 1])}, ${markLabel(mark)}`}
                aria-pressed={mark !== 0}
                tabIndex={focusedCell === index ? 0 : -1}
                disabled={completed}
                onFocus={() => setFocusedCell(index)}
                onPointerDown={(event) => pointerDown(event, index)}
                onKeyDown={(event) => keyDown(event, index)}
              >
                {mark === -1 && <span aria-hidden="true">×</span>}
              </button>
            );
          })}
        </div>
      </div>
      {mapVisible && (
        <div
          ref={mapRef}
          className={mapCollapsed ? "board-map is-collapsed" : "board-map"}
          style={mapPos ? { left: `${mapPos.left}px`, top: `${mapPos.top}px` } : { visibility: "hidden" }}
        >
          <button
            className="board-map-toggle"
            type="button"
            aria-label={mapCollapsed ? "Show board map" : "Hide board map"}
            aria-expanded={!mapCollapsed}
            onClick={() => setMapCollapsed((collapsed) => !collapsed)}
          >
            {mapCollapsed ? "⊞" : "×"}
          </button>
          {/* The grid picture is scenery — the board itself carries the same
              information for a keyboard or screen reader. The toggle is a real
              control and stays outside aria-hidden. */}
          {!mapCollapsed && (
            <div
              className="board-map-grid"
              style={{ "--size": puzzle.size } as CSSProperties}
              aria-hidden="true"
              onPointerDown={mapJump}
            >
              {cells.map((mark, index) => (
                <i key={index} className={mark === 1 ? "is-fill" : mark === -1 ? "is-cross" : undefined} />
              ))}
              <span
                className="board-map-frame"
                style={{
                  left: `${viewFrame.left * 100}%`,
                  top: `${viewFrame.top * 100}%`,
                  width: `${Math.max(viewFrame.width, 0.08) * 100}%`,
                  height: `${Math.max(viewFrame.height, 0.08) * 100}%`,
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
